// Browser-side copy of the data files (Cache API), so a reload doesn't fetch
// them again. Content-hashed files (clouds/<id>.<hash>.*, orig/…,
// overlays.<hash>.json) and edit ops (named by their hash) are served from it:
// a name never points at other bytes, so an entry is never stale, only unused
// once a publish renames the file; prune() drops those. The few files that do
// change (scene.json, manifest.json, volumes.json) go to the network first and
// fall back to the kept copy only when it can't be reached (fetchFresh). Files the editor saves (a scan's
// weights) are kept with their ETag and asked for again only if changed (fetchTagged; putTagged after a save).
// Without the Cache API (http, private modes, quota) everything just fetches.
const NAME = 'cloudview-data-v1';

let opened: Promise<Cache | null> | null = null;
function store(): Promise<Cache | null> {
  opened ??= (async () => {
    try { return typeof caches === 'undefined' ? null : await caches.open(NAME); } catch { return null; }
  })();
  return opened;
}

/** Key for a byte slice of a file (a query, since cache matching ignores fragments). */
export const sliceKey = (url: string, offset: number, bytes: number) => `${url}${url.includes('?') ? '&' : '?'}cvslice=${offset}-${bytes}`;

/** The cached bytes for key, or null. */
export async function get(key: string): Promise<ArrayBuffer | null> {
  const c = await store();
  if (!c) return null;
  try { const r = await c.match(key); return r ? await r.arrayBuffer() : null; } catch { return null; }
}

/** Keep bytes under key (best effort: a full or blocked store just doesn't keep them). */
export async function put(key: string, data: ArrayBuffer | Uint8Array) {
  const c = await store();
  if (!c) return;
  try { await c.put(key, new Response(data as BodyInit, { headers: { 'Content-Type': 'application/octet-stream' } })); } catch { /* quota or blocked */ }
}

export const bytesFetched = { n: 0 };

/** A whole content-hashed file: from the cache, else fetched and kept. */
export async function fetchBuf(url: string): Promise<ArrayBuffer> {
  const hit = await get(url);
  if (hit) return hit;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url}: HTTP ${r.status}`);
  const b = await r.arrayBuffer();
  bytesFetched.n += b.byteLength;
  put(url, b);
  return b;
}

export async function fetchJson<T>(url: string): Promise<T> { return JSON.parse(new TextDecoder().decode(await fetchBuf(url))) as T; }

/**
 * Drop cached entries under prefix whose file (path without the query) isn't in keep (a keep ending in '/': a
 * directory, everything in it kept): what a publish replaced. Entries elsewhere are left alone.
 */
export async function prune(prefix: string, keep: Set<string>) {
  const c = await store();
  if (!c) return;
  try {
    const abs = new URL(prefix, location.href).href, keepAbs = new Set([...keep].map(k => new URL(k, location.href).href));
    const dirs = [...keepAbs].filter(k => k.endsWith('/'));
    for (const req of await c.keys()) {
      const u = new URL(req.url), file = u.origin + u.pathname;
      if (file.startsWith(abs) && !keepAbs.has(file) && !dirs.some(d => file.startsWith(d))) await c.delete(req);
    }
  } catch { /* blocked */ }
}

/** Approximate bytes this origin keeps (all storage), or null if unknown. */
export async function usage(): Promise<number | null> {
  try { return (await navigator.storage?.estimate?.())?.usage ?? null; } catch { return null; }
}

export async function clear() {
  opened = null;
  try { await caches.delete(NAME); } catch { /* none */ }
}

export interface Part { offset: number; bytes: number }

/**
 * Byte slices of a file, each kept under its own key: those in the cache from there,
 * the rest with one Range request from the first missing to the last. net: bytes fetched.
 */
export async function fetchParts(url: string, parts: Part[], signal?: AbortSignal): Promise<{ bufs: ArrayBuffer[]; net: number }> {
  const bufs = await Promise.all(parts.map(p => get(sliceKey(url, p.offset, p.bytes))));
  const miss = parts.filter((p, i) => !bufs[i] || bufs[i]!.byteLength !== p.bytes);
  let net = 0;
  if (miss.length) {
    const a = Math.min(...miss.map(p => p.offset)), z = Math.max(...miss.map(p => p.offset + p.bytes));
    const res = await fetch(url, { headers: { Range: `bytes=${a}-${z - 1}` }, signal });
    if (res.status !== 206 && res.status !== 200) throw new Error(`${url}: HTTP ${res.status}`);
    const b = await res.arrayBuffer(), base = res.status === 200 ? 0 : a;
    net = b.byteLength;
    bytesFetched.n += net;
    for (const p of miss) {
      const x = b.slice(p.offset - base, p.offset - base + p.bytes);
      bufs[parts.indexOf(p)] = x;
      put(sliceKey(url, p.offset, p.bytes), x);
    }
  }
  return { bufs: bufs as ArrayBuffer[], net };
}

/** A file that can change: from the network (and kept), or the kept copy when the network fails. */
export async function fetchFresh(url: string): Promise<ArrayBuffer> {
  try {
    const r = await fetch(url, { cache: 'no-cache' });
    if (!r.ok) throw new Error(`${url}: HTTP ${r.status}`);
    const b = await r.arrayBuffer();
    put(url, b);
    return b;
  } catch (e) {
    const hit = await get(url);
    if (hit) { offline.used = true; return hit; }
    throw e;
  }
}
/** Keep bytes under key with their ETag (fetchTagged asks the server whether they are still current). */
export async function putTagged(key: string, data: ArrayBuffer | Uint8Array, tag: string) {
  const c = await store();
  if (!c) return;
  try { await c.put(key, new Response(data as BodyInit, { headers: { 'Content-Type': 'application/octet-stream', ETag: tag } })); } catch { /* quota or blocked */ }
}

/**
 * A file the editor saves: the kept copy when the server says it is unchanged (304 to its ETag), else fetched and
 * kept; null when there is none (404: the kept copy goes too); the kept copy when the server can't be reached.
 */
export async function fetchTagged(url: string): Promise<ArrayBuffer | null> {
  const c = await store();
  const kept = c ? await c.match(url).catch(() => undefined) : undefined, tag = kept?.headers.get('ETag');
  let r: Response;
  try { r = await fetch(url, { cache: 'no-store', headers: tag ? { 'If-None-Match': tag } : {} }); } catch (e) {
    if (kept) { offline.used = true; return kept.arrayBuffer(); }
    throw e;
  }
  if (r.status === 304 && kept) return kept.arrayBuffer();
  if (r.status === 404) { if (kept) await c!.delete(url).catch(() => false); return null; }
  if (!r.ok) throw new Error(`${url}: HTTP ${r.status}`);
  const b = await r.arrayBuffer(), t = r.headers.get('ETag');
  bytesFetched.n += b.byteLength;
  if (t) void putTagged(url, b, t);
  return b;
}

export const fetchFreshJson = async <T>(url: string): Promise<T> => JSON.parse(new TextDecoder().decode(await fetchFresh(url))) as T;
/** Set when a file that can change came from the kept copy (the network failed). */
export const offline = { used: false };

/** Every key in the cache, as absolute URLs (empty without a cache). */
export async function keys(): Promise<Set<string>> {
  const c = await store();
  if (!c) return new Set();
  try { return new Set((await c.keys()).map(r => r.url)); } catch { return new Set(); }
}
export const abs = (u: string) => new URL(u, location.href).href;

/** Ask the browser not to clear what is kept here when it runs short of space. True if granted. */
export async function persist(): Promise<boolean> {
  try {
    if (await navigator.storage?.persisted?.()) return true;
    return (await navigator.storage?.persist?.()) ?? false;
  } catch { return false; }
}
export const available = async () => !!(await store());
